/**
 * JSONPropertyEditor
 *
 * Reusable JSON object editor component for property editors
 * SRP: Responsible ONLY for rendering JSON input with validation
 * Used by: metadata, configuration, dataAttributes, customProps, etc.
 */

import React, { useCallback, useMemo } from 'react';

export interface JSONPropertyEditorProps {
  /** Display label for the input */
  label: string;

  /** Current JSON value (as string) */
  value: string;

  /** Called when value changes */
  onChange: (value: string) => void;

  /** Field is required */
  required?: boolean;

  /** Field is disabled */
  disabled?: boolean;

  /** Help text shown below input */
  helpText?: string;

  /** Number of rows for textarea */
  rows?: number;

  /** Show format button */
  showFormatButton?: boolean;

  /** Show pretty print toggle */
  showPrettyPrint?: boolean;

  /** Custom CSS class */
  className?: string;
}

/**
 * JSONPropertyEditor component
 * Renders a JSON textarea with validation and formatting
 */
export const JSONPropertyEditor: React.FC<JSONPropertyEditorProps> = ({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  helpText,
  rows = 8,
  showFormatButton = true,
  showPrettyPrint = true,
  className = '',
}) => {
  // Memoize handler to prevent unnecessary re-renders
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(event.currentTarget.value);
    },
    [onChange],
  );

  const handleFormat = useCallback(() => {
    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);
      onChange(formatted);
    } catch {
      // Silently fail if JSON is invalid
    }
  }, [value, onChange]);

  const handleMinify = useCallback(() => {
    try {
      const parsed = JSON.parse(value);
      const minified = JSON.stringify(parsed);
      onChange(minified);
    } catch {
      // Silently fail if JSON is invalid
    }
  }, [value, onChange]);

  const inputId = useMemo(() => `json-editor-${label.replace(/\s+/g, '-').toLowerCase()}`, [label]);

  // Validate JSON
  const isValidJson = useMemo(() => {
    if (!value.trim()) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }, [value]);

  // Parse error message
  const jsonError = useMemo(() => {
    if (isValidJson || !value.trim()) return null;
    try {
      JSON.parse(value);
      return null;
    } catch (error) {
      if (error instanceof SyntaxError) {
        return error.message;
      }
      return 'Invalid JSON';
    }
  }, [value, isValidJson]);

  const lineCount = useMemo(() => {
    return value.split('\n').length;
  }, [value]);

  const isPrettyPrinted = useMemo(() => {
    if (!isValidJson || !value.trim()) return false;
    return value.includes('\n');
  }, [value, isValidJson]);

  return (
    <div className={`af-property-editor__group ${className}`}>
      <div className="af-property-editor__json-header">
        <label htmlFor={inputId} className="af-property-editor__label">
          {label}
          {required && <span className="af-property-editor__required">*</span>}
        </label>

        <div className="af-property-editor__json-controls">
          {showFormatButton && isValidJson && (
            <button
              type="button"
              onClick={handleFormat}
              disabled={disabled || !isValidJson}
              className="af-property-editor__button af-property-editor__button--small"
              aria-label="Format JSON"
              title="Pretty print JSON"
            >
              Format
            </button>
          )}

          {showPrettyPrint && isValidJson && isPrettyPrinted && (
            <button
              type="button"
              onClick={handleMinify}
              disabled={disabled || !isValidJson}
              className="af-property-editor__button af-property-editor__button--small"
              aria-label="Minify JSON"
              title="Minify JSON"
            >
              Minify
            </button>
          )}
        </div>
      </div>

      <div className="af-property-editor__json-wrapper">
        <div className="af-property-editor__line-numbers" aria-hidden="true">
          {Array.from({ length: lineCount }, (_, i) => (
            <div key={i + 1} className="af-property-editor__line-number">
              {i + 1}
            </div>
          ))}
        </div>

        <textarea
          id={inputId}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          required={required}
          rows={rows}
          className={`af-property-editor__textarea af-property-editor__textarea--json ${
            !isValidJson && value ? 'af-property-editor__textarea--error' : ''
          }`}
          aria-label={label}
          aria-required={required}
          aria-invalid={!isValidJson && value !== ''}
          aria-describedby={helpText || jsonError ? `${inputId}-help` : undefined}
          spellCheck="false"
        />
      </div>

      <div className="af-property-editor__json-info">
        <span className="af-property-editor__json-stat">
          {value.length} character{value.length !== 1 ? 's' : ''}
        </span>
        {isValidJson && value.trim() && (
          <span className="af-property-editor__json-stat af-property-editor__json-stat--valid">
            Valid JSON
          </span>
        )}
      </div>

      {!isValidJson && value && (
        <div className="af-property-editor__error">
          <div className="af-property-editor__error-title">Invalid JSON</div>
          {jsonError && (
            <div className="af-property-editor__error-detail">
              {jsonError}
            </div>
          )}
        </div>
      )}

      {(helpText || jsonError) && (
        <div id={`${inputId}-help`} className="af-property-editor__help">
          {helpText}
        </div>
      )}
    </div>
  );
};

JSONPropertyEditor.displayName = 'JSONPropertyEditor';
